import jsPDF from 'jspdf';
import {format} from 'date-fns';

export const generatePDF=async (invoice)=> {
  try {
    const doc=new jsPDF({orientation: 'portrait',unit: 'pt',format: 'a4'});
    const pageWidth=doc.internal.pageSize.getWidth();
    const pageHeight=doc.internal.pageSize.getHeight();
    const margin=50;
    let y=0;

    const currencies={USD: {symbol: '$',name: 'US Dollar',portugueseName: 'Dólar Americano'},EUR: {symbol: '€',name: 'Euro',portugueseName: 'Euro'},BRL: {symbol: 'R$',name: 'Brazilian Real',portugueseName: 'Real Brasileiro'}};

    const cnaeOptions={'7911-2/00': 'Agências de viagens','7319-0/03': 'Marketing direto'};

    const getCurrencySymbol=(code)=> currencies[code]?.symbol || '$';
    const getCurrencyPortugueseName=(code)=> currencies[code]?.portugueseName || 'Dólar Americano';
    const currencySymbol=getCurrencySymbol(invoice.currency);

    const invoiceNumber = invoice.fullNumber || `INV-${invoice.number}`;

    // --- HEADER WITH DARK BLUE BAR ---
    doc.setFillColor(30,58,138);
    doc.rect(0,0,pageWidth,25,'F');
    y=50;

    // --- LOGO AND INVOICE TITLE ---
    if (invoice.companyInfo?.logoUrl) {
      try {
        const img = new Image();
        img.src = invoice.companyInfo.logoUrl;
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
        });
        
        const maxWidth = 120;
        const maxHeight = 40;
        const imgAspectRatio = img.width / img.height;
        const boxAspectRatio = maxWidth / maxHeight;

        let logoWidth;
        let logoHeight;

        if (imgAspectRatio > boxAspectRatio) {
          logoWidth = maxWidth;
          logoHeight = maxWidth / imgAspectRatio;
        } else {
          logoHeight = maxHeight;
          logoWidth = maxHeight * imgAspectRatio;
        }
        
        doc.addImage(invoice.companyInfo.logoUrl, 'PNG', margin, y, logoWidth, logoHeight);
      } catch (e) {
        console.log('Logo could not be loaded.');
      }
    }

    doc.setFont('helvetica','bold');
    doc.setFontSize(32);
    doc.setTextColor(30,58,138);
    doc.text('INVOICE',pageWidth - margin,y + 10,{align: 'right'});

    // --- STATUS LABEL ---
    const status = invoice.status || 'unpaid';
    const isPaid = status === 'paid';
    const statusText = isPaid ? 'PAID' : 'UNPAID';
    const statusColor = isPaid ? [0, 128, 0] : [220, 38, 38];

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(statusColor[0], statusColor[1], statusColor[2]);
    doc.text(statusText, pageWidth - margin, y + 30, { align: 'right' });

    doc.setFont('helvetica','normal');
    doc.setFontSize(11);
    doc.setTextColor(75,85,99);
    if (invoice.isExportation) {
        doc.text(`Invoice #: ${invoiceNumber}`,pageWidth - margin,y + 48,{align: 'right'});
    } else {
        doc.text(`Invoice #: ${invoiceNumber}`,pageWidth - margin,y + 48,{align: 'right'});
        doc.text(`Date: ${format(new Date(invoice.createdAt || Date.now()),'MMMM dd,yyyy')}`,pageWidth - margin,y + 62,{align: 'right'});
    }
    
    y += 70;
    
    doc.setFont('helvetica','bold');
    doc.setFontSize(14);
    doc.setTextColor(30,58,138);
    doc.text('BILLING INFORMATION',margin,y);
    y +=25;

    // --- BILLING INFO CARDS ---
    const cardWidth=(pageWidth - (margin * 2) - 20) / 2;
    const cardHeight=120;
    
    // Customer card
    doc.setFillColor(249,250,251);
    doc.roundedRect(margin - 2,y - 5,cardWidth + 4,cardHeight + 4,8,8,'F');
    doc.setDrawColor(229,231,235);
    doc.roundedRect(margin - 2,y - 5,cardWidth + 4,cardHeight + 4,8,8);
    
    doc.setFillColor(30,58,138);
    doc.roundedRect(margin,y - 2,cardWidth,30,6,6,'F');
    
    doc.setFont('helvetica','bold');
    doc.setFontSize(12);
    doc.setTextColor(255,255,255);
    doc.text('CLIENT INFORMATION',margin + 15,y + 15);
    
    doc.setTextColor(55,65,81);
    let customerY=y + 45;
    
    doc.setFont('helvetica','bold');
    doc.setFontSize(12);
    doc.text(invoice.customer?.name || 'Customer Name',margin + 15,customerY);
    
    doc.setFont('helvetica','normal');
    doc.setFontSize(10);
    customerY +=18;
    
    if (invoice.customer?.address) {
      const addressLines=doc.splitTextToSize(invoice.customer.address,cardWidth - 30);
      addressLines.forEach(line=> {
        doc.text(line,margin + 15,customerY);
        customerY +=14;
      });
    }
    if (invoice.customer?.phone) {
      doc.text(invoice.customer.phone,margin + 15,customerY);
      customerY +=14;
    }
    if (invoice.customer?.email && invoice.showEmail) {
      doc.text(invoice.customer.email,margin + 15,customerY);
      customerY +=14;
    }

    // Company card
    const companyX=margin + cardWidth + 20;
    
    doc.setFillColor(249,250,251);
    doc.roundedRect(companyX - 2,y - 5,cardWidth + 4,cardHeight + 4,8,8,'F');
    doc.setDrawColor(229,231,235);
    doc.roundedRect(companyX - 2,y - 5,cardWidth + 4,cardHeight + 4,8,8);
    
    doc.setFillColor(75,85,99);
    doc.roundedRect(companyX,y - 2,cardWidth,30,6,6,'F');
    
    doc.setFont('helvetica','bold');
    doc.setFontSize(12);
    doc.setTextColor(255,255,255);
    doc.text('COMPANY DETAILS',companyX + 15,y + 15);
    
    doc.setTextColor(55,65,81);
    let companyY=y + 45;
    
    doc.setFont('helvetica','bold');
    doc.setFontSize(12);
    doc.text(invoice.companyInfo?.name || 'Your Company',companyX + 15,companyY);
    
    doc.setFont('helvetica','normal');
    doc.setFontSize(10);
    companyY +=18;
    
    if (invoice.companyInfo?.address) {
      const addressLines=doc.splitTextToSize(invoice.companyInfo.address,cardWidth - 30);
      addressLines.forEach(line=> {
        doc.text(line,companyX + 15,companyY);
        companyY +=14;
      });
    }
    if (invoice.companyInfo?.phone) {
      doc.text(`Phone: ${invoice.companyInfo.phone}`,companyX + 15,companyY);
      companyY +=14;
    }
    if (invoice.companyInfo?.email) {
      doc.text(`Email: ${invoice.companyInfo.email}`,companyX + 15,companyY);
      companyY +=14;
    }

    y=y + cardHeight + 40;

    // --- EXPORTATION SECTION ---
    if (invoice.isExportation) {
      const exportBoxY=y;
      const exportBoxWidth=pageWidth - (margin * 2);
      doc.setFillColor(254,226,226);
      doc.roundedRect(margin,exportBoxY,exportBoxWidth,70,5,5,'F');
      
      doc.setFillColor(220,38,38);
      doc.rect(margin,exportBoxY,4,70,'F');
      
      doc.setTextColor(185,28,28);
      doc.setFont('helvetica','bold');
      doc.setFontSize(20);
      doc.text('EXPORTAÇÃO',margin + 20,exportBoxY + 25);
      
      doc.setFont('helvetica','normal');
      doc.setFontSize(11);
      const selectedCnae=invoice.cnae || '7319-0/03';
      const activityName=cnaeOptions[selectedCnae] || 'Marketing direto';
      const cnaeText=`CNAE: ${selectedCnae} - ${activityName}`;
      doc.text(cnaeText,margin + 20,exportBoxY + 42);
      
      const formatPortugueseDate=(date)=> format(new Date(date),'dd/MM/yyyy');
      
      let dateText;
      if (invoice.isDateRange && invoice.paymentDate && invoice.paymentEndDate) {
        dateText=`Período: ${formatPortugueseDate(invoice.paymentDate)} a ${formatPortugueseDate(invoice.paymentEndDate)}`;
      } else if (invoice.paymentDate) {
        dateText=`Data: ${formatPortugueseDate(invoice.paymentDate)}`;
      } else {
        dateText=`Data: ${formatPortugueseDate(new Date())}`;
      }
      doc.text(dateText,margin + 20,exportBoxY + 58);
      
      doc.text(`Moeda: ${getCurrencyPortugueseName(invoice.currency)}`,margin + 250,exportBoxY + 58);
      
      y +=90;
    }

    // --- ITEMS TABLE ---
    const tableHeaders=['Item Description','Qty','Unit Price','Total'];
    const tableStartX=margin;
    const tableWidth=pageWidth - (margin * 2);
    const colWidths=[tableWidth * 0.5,tableWidth * 0.15,tableWidth * 0.175,tableWidth * 0.175];

    doc.setFillColor(30,58,138);
    doc.roundedRect(tableStartX,y,tableWidth,30,5,5,'F');
    
    doc.setFont('helvetica','bold');
    doc.setFontSize(12);
    doc.setTextColor(255,255,255);
    
    let currentX=tableStartX + 15;
    doc.text(tableHeaders[0],currentX,y + 18);
    currentX +=colWidths[0];
    doc.text(tableHeaders[1],currentX + colWidths[1] / 2,y + 18,{align: 'center'});
    currentX +=colWidths[1];
    doc.text(tableHeaders[2],currentX + colWidths[2] / 2,y + 18,{align: 'center'});
    currentX +=colWidths[2];
    doc.text(tableHeaders[3],currentX + colWidths[3] / 2,y + 18,{align: 'center'});
    
    y +=30;

    (invoice.items || []).forEach((item,index)=> {
      const nameText=item.name || 'Item';
      const descriptionText=item.description || '';
      const nameLines=doc.splitTextToSize(nameText,colWidths[0] - 20);
      const descriptionLines=descriptionText ? doc.splitTextToSize(descriptionText,colWidths[0] - 20) : [];
      const rowHeight=(nameLines.length * 14) + (descriptionLines.length * 12) + 25;

      if (y + rowHeight > pageHeight - 150) {
        doc.addPage();
        y=margin;
      }

      doc.setFillColor(243,244,246);
      doc.rect(tableStartX,y,tableWidth,rowHeight,'F');

      let lineY=y + 15;
      
      doc.setFont('helvetica','bold');
      doc.setFontSize(11);
      doc.setTextColor(31,41,55);
      doc.text(nameLines,tableStartX + 15,lineY);
      lineY +=nameLines.length * 14;

      if (descriptionLines.length > 0) {
        doc.setFont('helvetica','italic');
        doc.setFontSize(9);
        doc.setTextColor(107,114,128);
        doc.text(descriptionLines,tableStartX + 15,lineY);
      }

      const quantity=parseFloat(item.quantity) || 0;
      const price=parseFloat(item.price) || 0;
      const total=quantity * price;

      doc.setFont('helvetica','normal');
      doc.setFontSize(11);
      doc.setTextColor(31,41,55);
      
      let colX=tableStartX + colWidths[0];
      doc.text(String(quantity),colX + colWidths[1] / 2,y + 20,{align: 'center'});
      colX +=colWidths[1];
      doc.text(`${currencySymbol}${price.toFixed(2)}`,colX + colWidths[2] / 2,y + 20,{align: 'center'});
      colX +=colWidths[2];
      doc.setFont('helvetica','bold');
      doc.text(`${currencySymbol}${total.toFixed(2)}`,colX + colWidths[3] / 2,y + 20,{align: 'center'});

      y +=rowHeight;
    });

    y +=20;

    // --- TOTALS SECTION ---
    const subtotal=(invoice.items || []).reduce((sum,item)=> (sum + (parseFloat(item.quantity) || 0) * (parseFloat(item.price) || 0)),0);
    const totalAmount=subtotal + (parseFloat(invoice.processingFee) || 0) + (invoice.includeTax ? subtotal * ((parseFloat(invoice.taxRate) || 0) / 100) : 0);

    const totalsBoxWidth=250;
    const totalsBoxX=pageWidth - margin - totalsBoxWidth;

    doc.setFillColor(30,58,138);
    doc.roundedRect(totalsBoxX,y,totalsBoxWidth,30,5,5,'F');

    doc.setFont('helvetica','bold');
    doc.setFontSize(12);
    doc.setTextColor(255,255,255);
    doc.text('INVOICE TOTAL',totalsBoxX + totalsBoxWidth / 2,y + 18,{align: 'center'});
    y +=30;

    const totalsContentHeight=80;
    doc.setFillColor(243,244,246);
    doc.roundedRect(totalsBoxX,y,totalsBoxWidth,totalsContentHeight,5,5,'F');
    
    let totalY=y + 25;
    doc.setFont('helvetica','normal');
    doc.setFontSize(11);
    doc.setTextColor(55,65,81);
    doc.text('Subtotal:',totalsBoxX + 20,totalY);
    doc.text(`${currencySymbol}${subtotal.toFixed(2)}`,totalsBoxX + totalsBoxWidth - 20,totalY,{align: 'right'});
    
    totalY +=20;
    doc.setDrawColor(209,213,219);
    doc.setLineWidth(1);
    doc.line(totalsBoxX + 15,totalY,totalsBoxX + totalsBoxWidth - 15,totalY);
    
    totalY +=20;
    doc.setFont('helvetica','bold');
    doc.setFontSize(16);
    doc.setTextColor(75,85,99);
    doc.text('TOTAL:',totalsBoxX + 20,totalY);
    doc.text(`${currencySymbol}${totalAmount.toFixed(2)}`,totalsBoxX + totalsBoxWidth - 20,totalY,{align: 'right'});

    y +=totalsContentHeight + 30;

    // --- NOTES SECTION ---
    if (invoice.notes && invoice.notes.trim()) {
      doc.setFont('helvetica','bold');
      doc.setFontSize(12);
      doc.setTextColor(30,58,138);
      doc.text('Notes:',margin,y);
      
      doc.setFont('helvetica','normal');
      doc.setFontSize(9);
      doc.setTextColor(75,85,99);
      
      const noteLines=doc.splitTextToSize(invoice.notes,pageWidth - (margin * 2));
      noteLines.forEach(line=> {
        doc.text(line,margin,y + 15);
        y +=12;
      });
      
      y +=15;
    }

    // --- FOOTER ---
    const footerY=pageHeight - 40;
    doc.setFontSize(10);
    doc.setTextColor(107,114,128);
    doc.text('Thank you for your business!',pageWidth / 2,footerY,{align: 'center'});

    // --- SAVE ---
    const customerName=(invoice.customer?.name || 'Customer').replace(/[^a-zA-Z0-9]/g,'_');
    const paymentDate=format(new Date(invoice.paymentDate || Date.now()),'yyyy-MM-dd');
    const filename=`${customerName}_invoice_${invoiceNumber.replace('-', '_')}_${paymentDate}.pdf`;
    
    doc.save(filename);

  } catch (error) {
    console.error('Error generating PDF:',error);
    throw new Error(`Failed to generate PDF: ${error.message}`);
  }
};